import ModalViewModel from '../../../../../infra/ModalViewModel'
import { action, computed, makeObservable, observable, override } from 'mobx'
import {
  ProgressionEditorViewModel,
  RegularEditorViewModel,
} from './BillingEditorViewModel'
import {
  AvailableProgressionOption,
  AvailableProgressionStep,
  AvailableProgressionSteps,
  SelectedProgression,
  StepMap,
} from './declarations'
import { required, validationContext } from 'validx'

export default class JobProgressionBillingViewModel extends ModalViewModel {
  /**
   * The given progression for this modal
   */
  @observable
  selectedProgression?: AvailableProgressionOption = undefined

  /**
   * The selected steps for this progression
   */
  @observable
  selectedSteps: StepMap = new Map()

  /**
   * Validation to be used on this modal
   */
  validation = validationContext(this, {
    selectedProgression: [required('This field is required.')],
    selectedSteps: [
      ({ value }) => value.size > 0 || 'Select at least one step',
    ],
  })

  constructor(private progressionEditor: ProgressionEditorViewModel) {
    super()
    makeObservable(this)
  }

  /**
   * Non selected available progressions
   */
  @computed
  get availableProgressions() {
    return this.progressionEditor.availableProgressions
  }

  /**
   * Available steps for this progression
   */
  @computed
  get availableSteps(): AvailableProgressionStep[] {
    return this.selectedProgression
      ? AvailableProgressionSteps[this.selectedProgression.value]
      : []
  }

  /**
   * Steps selected to be used with this progression
   */
  @computed
  get selectedStepsList() {
    return Array.from(this.selectedSteps.values())
  }

  /**
   * Triggers the modal
   */
  @override
  show(selectedProgression?: SelectedProgression) {
    if (selectedProgression) {
      const { name, value, steps } = selectedProgression

      this.selectedProgression = { name, value }

      steps.forEach((step) => {
        this.selectedSteps.set(step.value, step)
      })
    }

    super.show()
  }

  /**
   * Dismisses the modal
   */
  @override
  close() {
    this.reset()
    super.close()
  }

  /**
   * Selects the progression to be used
   */
  @action.bound
  setSelectedProgression(progression: AvailableProgressionOption) {
    this.selectedProgression = progression
    this.selectedSteps.clear()
  }

  /**
   * Assigns the steps for this progression
   */
  @action.bound
  setSelectedSteps(steps: AvailableProgressionStep[]) {
    this.selectedSteps = new Map(
      steps.map((step) => [
        step.value,
        this.selectedSteps.get(step.value) ?? {
          ...step,
          pricingVM: new RegularEditorViewModel(
            this.progressionEditor.billingType,
          ),
        },
      ]),
    )
  }

  /**
   * Clears the values
   */
  @action.bound
  reset() {
    this.selectedProgression = undefined
    this.selectedSteps.clear()
  }

  /**
   * Validates this modal
   */
  validate() {
    return (
      this.validation.reset().validate().isValid &&
      Array.from(this.selectedSteps.values()).every((step) =>
        step.pricingVM.validate(),
      )
    )
  }

  /**
   * Saves this data
   */
  @action.bound
  save() {
    if (this.validate() && this.selectedProgression) {
      this.progressionEditor.setProgression(this.selectedProgression.value, {
        ...this.selectedProgression,
        steps: Array.from(this.selectedSteps.values()),
      })

      this.close()
    }
  }
}
